import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { StartExperimentComponent } from '../../components/start-experiment/start-experiment.component';
import { OngoingExperimentListComponent } from '../../components/ongoing-experiment-list/ongoing-experiment-list.component';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { ToBeUploadedExperimentListComponent } from '../../components/to-be-uploaded-experiment-list/to-be-uploaded-experiment-list.component';
import { CommonModule } from '@angular/common';
import { PasswordGuardComponent } from '../../components/password-guard/password-guard.component';
import { ExcelExportButtonComponent } from '../../components/excel-export-button/excel-export-button.component';
import { UploadDataComponent } from '../upload-data/upload-data.component';
import { NavigationBarComponent } from '../../components/navigation-bar/navigation-bar.component';
import { Subscription, combineLatest } from 'rxjs';

@Component({
  selector: 'app-home',
  imports: [
    CommonModule,
    StartExperimentComponent,
    OngoingExperimentListComponent,
    ToBeUploadedExperimentListComponent,
    PasswordGuardComponent,
    ExcelExportButtonComponent,
    UploadDataComponent,
    NavigationBarComponent,
  ],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
})
export class HomeComponent implements OnInit, OnDestroy {
  isAuthenticated = false;
  isTokenExpired = false;
  isPasswordVerified = false;
  
  // Upload popup properties
  showUploadPopup = false;
  selectedParticipantId = '';

  private authSubscription?: Subscription;

  // ViewChild references to excel export buttons
  @ViewChild('exportButton1') exportButton1!: ExcelExportButtonComponent;
  @ViewChild('exportButton2') exportButton2!: ExcelExportButtonComponent;
  @ViewChild('exportButton3') exportButton3!: ExcelExportButtonComponent;
  @ViewChild('exportButton4') exportButton4!: ExcelExportButtonComponent;
  @ViewChild('exportButton5') exportButton5!: ExcelExportButtonComponent;
  @ViewChild('exportButton6') exportButton6!: ExcelExportButtonComponent;

  constructor(private oidcSecurityService: OidcSecurityService) {}

  ngOnInit() {
    // Check both authentication status and token validity
    this.authSubscription = combineLatest([
      this.oidcSecurityService.isAuthenticated(),
      this.oidcSecurityService.getAccessToken()
    ]).subscribe(([isAuthenticated, accessToken]) => {
      // User is authenticated if they have auth status AND a valid token
      this.isAuthenticated = isAuthenticated && !!accessToken && accessToken.trim() !== '';
      
      // Check token expiry if we have a token
      if (accessToken) {
        this.checkTokenExpiry(accessToken);
      } else {
        this.isTokenExpired = true;
      }
    });
  }

  ngOnDestroy() {
    this.authSubscription?.unsubscribe();
  }

  private checkTokenExpiry(token: string) {
    try {
      const decoded = this.decodeJWT(token);
      if (decoded && decoded.exp) {
        const currentTime = Math.floor(Date.now() / 1000);
        this.isTokenExpired = decoded.exp < currentTime;
      } else {
        this.isTokenExpired = true;
      }
    } catch (error) {
      console.error('Error checking token expiry:', error);
      this.isTokenExpired = true;
    }
  }

  private decodeJWT(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding JWT:', error);
      return null;
    }
  }

  onPasswordVerified() {
    this.isPasswordVerified = true;
  }

  onUploadRequested(participantIdentifier: string) {
    this.selectedParticipantId = participantIdentifier;
    this.showUploadPopup = true;
  }

  async onUploadCompleted() {
    this.showUploadPopup = false;
    this.selectedParticipantId = '';
    
    // Refresh all excel export buttons to show newly uploaded data
    const exportButtons = [
      this.exportButton1,
      this.exportButton2,
      this.exportButton3,
      this.exportButton4,
      this.exportButton5,
      this.exportButton6
    ];

    // Refresh all export buttons in parallel
    await Promise.all(
      exportButtons
        .filter(button => button) // Only refresh buttons that exist
        .map(button => button.refresh())
    );
  }

  onUploadCancelled() {
    this.showUploadPopup = false;
    this.selectedParticipantId = '';
  }
}
